import type { Account } from '@adtrackify/at-tracking-event-types';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { useWorkspaceSwitch } from '@/features/workspace/hooks/use-workspace-switch';
import { WorkspaceSwitchOutcome } from '@/contexts/account/types/workspace-switch-outcome-enums';
import { useAccountState } from '@/contexts/account/account-context';
import { useSignupFlowStore } from '@/features/signup/stores/use-signup-flow-store';
import useStore from '@/store/app/use-store';

interface UseAccountSwitcherReturn {
  accounts: Account[];
  filteredAccounts: Account[];
  currentAccount: Account | null;
  isLoading: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSwitchAccount: (accountId: string) => Promise<void>;
  handleAddNewCompany: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function useAccountSwitcher(): UseAccountSwitcherReturn {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const { accountsList, currentAccount } = useAccountState();
  const { switchWorkspace, isSwitching } = useWorkspaceSwitch();

  const storedUser = useStore(s => s.user);

  const accounts = React.useMemo(() => {
    return [...accountsList].sort((a, b) => {
      const aIsActive = a.subscription?.status === 'active';
      const bIsActive = b.subscription?.status === 'active';
      if (aIsActive === bIsActive) return 0;
      return aIsActive ? -1 : 1;
    });
  }, [accountsList]);

  const filteredAccounts = React.useMemo(() => {
    if (!searchQuery || accounts.length <= 100) return accounts;
    return accounts.filter(account =>
      account.accountName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [accounts, searchQuery]);

  const handleSwitchAccount = React.useCallback(
    async (accountId: string) => {
      const result = await switchWorkspace(accountId, {
        userId: storedUser?.sub,
        onRequiresPlan: userId => {
          useSignupFlowStore.getState().setUserId(userId);
        },
      });

      if (result.outcome === WorkspaceSwitchOutcome.NO_OP) {
        setOpen(false);
        return;
      }

      // Close on real completion or failure — never on a timer.
      setOpen(false);
    },
    [switchWorkspace, storedUser?.sub]
  );

  const handleAddNewCompany = React.useCallback(() => {
    const email = storedUser?.email ?? '';
    void navigate(`/signup/continue?email=${encodeURIComponent(email)}`);
    setOpen(false);
  }, [storedUser?.email, navigate]);

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    const openState = nextOpen;
    setOpen(openState);
    if (!openState) {
      setSearchQuery('');
    }
  }, []);

  return {
    accounts,
    filteredAccounts,
    currentAccount,
    isLoading: isSwitching,
    searchQuery,
    setSearchQuery,
    handleSwitchAccount,
    handleAddNewCompany,
    open,
    setOpen: handleOpenChange,
  };
}
